import {Hidden, useMobile} from "@hps/hops-react";
import {CheckoutBasketItem} from "@hps/hops-sdk-js";
import {AppBar, Badge, Box, CardActionArea, Collapse, InputBase, IconButton as MuiIconButton, Toolbar} from "@mui/material";
import throttle from "lodash.throttle";
import React, {useEffect, useRef, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";

import Button from "Components/Button";
import Container from "Components/Container.js";
import Flex from "Components/Flex.js";
import IconButton from "Components/IconButton";
import Image from "Components/Image.js";
import Link from "Components/Link";
import useCheckoutBasket from "Hooks/useCheckoutBasket";
import useFeatures from "Hooks/useFeatures";
import useOrg from "Hooks/useOrg";
import * as icons from "Resources/Icons.js";
import Orgs from "Resources/Orgs";
import Routes from "Resources/Routes.json";
import AppService from "Services/AppService.js";

import UiBarDrawer from "./UiBarDrawer.js";

import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";

import scss from "./UiBar.module.scss";


const UiBar = () => {

	const location = useLocation();
	const navigate = useNavigate();
	const {checkoutBasket} = useCheckoutBasket();
	const {isFeatureEnabled} = useFeatures();
	const {bp, isMobile} = useMobile();
	const isDesktop = bp("lg");
	const {org} = useOrg();
	const isRaildays = isFeatureEnabled("RaildaysShell");

	const collapsedScrollPosition = () => (window.scrollY > 120);

	const [collapsed, setCollapsed] = useState(collapsedScrollPosition());
	const [menuOpen, setMenuOpen] = useState(false);
	const [searchOpen, setSearchOpen] = useState(false);
	const [searchValue, setSearchValue] = useState("");
	const searchInputRef = useRef(null);

	const height = (isMobile || collapsed) ? "9rem" : "12rem";

	const styles = {
		height,
		minHeight: height,
		transition: "all 0.2s ease-out",
		transitionProperty: "height, min-height"
	};

	useEffect(() => {
		const handleScroll = throttle(() => {
			setCollapsed(collapsedScrollPosition());
		}, 150);

		window.addEventListener("scroll", handleScroll);
		return () => window.removeEventListener("scroll", handleScroll);
	}, []);

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		setSearchValue(params.get("search") || "");
		setSearchOpen(false);
	}, [location.search]);

	const handleLogoClick = () => navigate(Routes.index);

	const toggleSearch = () => {
		setSearchOpen(open => {
			if (!open) {
				setTimeout(() => {
					searchInputRef.current?.focus();
					searchInputRef.current?.select();
				}, 50);
			}
			return !open;
		});
	};

	const executeSearch = () => {
		const query = searchValue.trim();
		if (!query) return;
		setSearchOpen(false);
		navigate(`/search?search=${encodeURIComponent(query)}`);
	};

	const handleSearchSubmit = e => {
		e.preventDefault();
		executeSearch();
	};

	const searchInput = (
		<InputBase
			inputRef={searchInputRef}
			placeholder="Search..."
			value={searchValue}
			onChange={e => setSearchValue(e.target.value)}
			endAdornment={
				<MuiIconButton
					type="submit"
					size="small"
					aria-label="Search"
					sx={{"color": "rgba(255,255,255,0.6)", "&:hover": {color: "#fff"}}}>
					<SearchIcon fontSize="small" />
				</MuiIconButton>
			}
			sx={{
				"color": "rgba(255,255,255,0.9)",
				"backgroundColor": "rgba(255,255,255,0.15)",
				"borderRadius": "4px",
				"pl": 1.5,
				"pr": 0.5,
				"py": 0.5,
				"& input::placeholder": {color: "rgba(255,255,255,0.5)"}
			}} />
	);

	return (
		<AppBar className={scss.AppBar} position="fixed" style={styles}>
			<Flex>
				<Toolbar className={scss.Toolbar} disableGutters={true} style={{...styles, position: "relative"}}>
					<Container
						alignContent="center"
						alignItems="center"
						className={scss.Container}
						columnar={true}
						gridTemplateRows="70%"
						height="100%"
						justifyContent="space-between"
						width="100%">
						<CardActionArea
							className={scss.LogoButton}
							onClick={handleLogoClick}>
							<Image
								alt={org.NameTrade}
								imgStyles={{height: "100%", objectFit: "contain"}}
								noError={true}
								noLoader={true}
								src={AppService.getRegistrationLogo()}
								style={{height: "100%", justifyContent: "center"}} />
						</CardActionArea>
						<Flex
							alignItems="center"
							className={(!isMobile ? scss.IconButtonContainer : scss.IconButtonContainer_small)}
							columnar={true}
							justifyContent="flex-end">
							<Link className={scss.NavIconLink} uri={"/"}>
								<IconButton
									classes={{root: scss.NavIcon}}
									defaultStyling={true}
									icon={icons.HomeIcon}
									label="Home"
									size="large" />
							</Link>
							{isDesktop && (
								<div className={scss.NavLinksContainer}>
									{org?.NavigationLinks?.map((link, key) => (
										<Link key={key} uri={link.Uri}>
											<Button
												classes={{root: location.pathname.startsWith(link.Uri) ? scss.NavLinkActive : scss.NavLink}}
												label={link.Label}
												variant="text" />
										</Link>
									))}
									{!isRaildays && (
										<Link uri={Routes.faqs}>
											<Button
												classes={{root: location.pathname.startsWith(Routes.faqs) ? scss.NavLinkActive : scss.NavLink}}
												label="FAQs"
												variant="text" />
										</Link>
									)}
								</div>
							)}
							{isRaildays && isDesktop && (
								<Box
									component="form"
									onSubmit={handleSearchSubmit}
									sx={{display: "flex", alignItems: "center", flexShrink: 0}}>
									<Collapse orientation="horizontal" in={searchOpen}>
										<Box sx={{width: 220, mr: 0.5}}>
											{searchInput}
										</Box>
									</Collapse>
									<IconButton
										classes={{root: scss.NavIcon}}
										defaultStyling={true}
										icon={searchOpen ? CloseIcon : SearchIcon}
										label={searchOpen ? "Close search" : "Search"}
										onClick={toggleSearch}
										size="large" />
								</Box>
							)}
							{isRaildays && !isDesktop && (
								<IconButton
									classes={{root: scss.NavIcon}}
									defaultStyling={true}
									icon={SearchIcon}
									label="Search"
									onClick={toggleSearch}
									size="large" />
							)}
							<Hidden hidden={isRaildays}>
								<Badge
									badgeContent={checkoutBasket?.filter(i => !CheckoutBasketItem.isInternal(i))?.reduce((n, {Quantity}) => n + Quantity, 0)}
									className={scss.CheckoutBadge}
									color="error">
									<Link uri={Routes.basket}>
										<IconButton
											classes={{root: location.pathname.startsWith(Routes.basket) ? scss.NavIconActive : scss.NavIcon}}
											defaultStyling={true}
											icon={icons.BasketIcon}
											label="Basket"
											size="large" />
									</Link>
								</Badge>
								<Hidden hidden={org.Id === Orgs.HOPS_HERITAGE_RAILWAYS}>
									<Link className={scss.NavIconLink} uri={Routes.account}>
										<IconButton
											classes={{root: location.pathname.startsWith(Routes.account) ? scss.NavIconActive : scss.NavIcon}}
											defaultStyling={true}
											icon={icons.AccountIcon}
											label="User Account"
											size="large" />
									</Link>
								</Hidden>
							</Hidden>
							{(!isDesktop && !!org?.NavigationLinks?.length) && (
								<IconButton
									classes={{root: scss.NavIcon}}
									defaultStyling={true}
									icon={icons.MenuIcon}
									label="Menu"
									onClick={() => setMenuOpen(true)} />
							)}
						</Flex>
					</Container>

					{isRaildays && !isDesktop && searchOpen && (
						<Box
							component="form"
							onSubmit={handleSearchSubmit}
							sx={{
								position: "absolute",
								inset: 0,
								display: "flex",
								alignItems: "center",
								px: 2,
								gap: 1,
								backgroundColor: "primary.main",
								zIndex: 1
							}}>
							<Box sx={{flex: 1}}>
								{searchInput}
							</Box>
							<IconButton
								classes={{root: scss.NavIcon}}
								defaultStyling={true}
								icon={CloseIcon}
								label="Close search"
								onClick={toggleSearch}
								size="large" />
						</Box>
					)}
				</Toolbar>
			</Flex>
			<UiBarDrawer
				navigationLinks={org?.NavigationLinks}
				onClose={() => setMenuOpen(false)}
				open={menuOpen}
				showFaqs={!isRaildays} />
		</AppBar>
	);

};

export default UiBar;
